import { useEffect, useState } from 'react';
import { Badge, Box, CloseButton, Drawer, Flex, Icon, Portal, Text } from '@chakra-ui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { PlanItem } from '@/lib/types';
import { slotFor } from '@/lib/slot-color';
import { toaster } from '@/components/ui/toaster';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import useDialogCleanup from '@/lib/hooks/use-dialog-cleanup';
import api from '@/utils/api';
import ViewContent from '@/pages/contents/view-content';
import '@/styles/print.css';

interface PlannerItemDrawerProps {
  isOpen: boolean;
  onClose(): void;
  item: PlanItem | null;
}

// Slide-over showing a content item's detail. Linked items fetch the
// full public/content/:ref payload and render it via the shared ViewContent
// component (same one contents/collections use for their own drawers).
// Custom items (no content_id) render a small header + a notice instead,
// since there's nothing in the library to show.
export default function PlannerItemDrawer({ isOpen, onClose, item }: PlannerItemDrawerProps) {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  useDialogCleanup(isOpen);

  const isLinked = !!item?.content_reference_id;

  useEffect(() => {
    if (!isOpen || !item || !isLinked) {
      setData(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    api
      .get(`public/content/${item.content_reference_id}`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data.data);
      })
      .catch((err: any) => {
        if (cancelled) return;
        toaster.create({
          type: 'error',
          title: err.response?.data?.error ?? 'Failed to load content detail',
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, item, isLinked]);

  const slot = item?.content_type_id != null ? slotFor(String(item.content_type_id)) : null;

  return (
    <Drawer.Root
      id='planner-item-drawer'
      size='xl'
      placement='end'
      lazyMount
      unmountOnExit
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content bg='bg.panel' shadow='lg'>
            <Drawer.CloseTrigger asChild>
              <CloseButton size='sm' position='absolute' top={4} insetEnd={4} zIndex={2} />
            </Drawer.CloseTrigger>
            <Drawer.Body>
              {!item ? null : isLinked ? (
                <div data-print-target>
                  {isLoading && (
                    <Box pt={10}>
                      <Skeleton height='8' width='60%' mb={4} />
                      <Skeleton height='40' mb={4} />
                      <SkeletonText noOfLines={4} />
                    </Box>
                  )}
                  {!isLoading && data && <ViewContent data={data} inDrawer onClose={onClose} />}
                  {!isLoading && !data && (
                    <Flex justify='center' py={10}>
                      <Text fontSize='sm' color='fg.muted'>
                        Content unavailable.
                      </Text>
                    </Flex>
                  )}
                </div>
              ) : (
                <Box pt={10}>
                  <Flex align='center' gap={2} mb={3} flexWrap='wrap'>
                    <Text fontFamily='heading' fontWeight='800' fontSize='h3' textTransform='uppercase' color='fg.strong'>
                      {item.name}
                    </Text>
                    {(item.content_type_name ?? item.custom_label) && (
                      <Badge
                        size='sm'
                        bg={slot ? `slot${slot}.bg` : 'bg.subtle'}
                        color={slot ? `slot${slot}.fg` : 'fg.muted'}
                      >
                        {item.content_type_name ?? item.custom_label}
                      </Badge>
                    )}
                  </Flex>

                  <Flex
                    gap={3}
                    bg='bg.subtle'
                    borderRadius='md'
                    p={3}
                    align='flex-start'
                  >
                    <Icon as={InformationCircleIcon} w={5} h={5} color='fg.muted' flexShrink={0} mt={0.5} />
                    <Text fontSize='sm' color='fg.muted'>
                      {item.content_type_name
                        ? `This is a custom item. Saving the plan will add it to your ${item.content_type_name} library as a draft, ready to flesh out with media, coaching points, and tags.`
                        : 'This is a custom item — not linked to a library entry. Pick a library label in the builder to add it to a content library on save.'}
                    </Text>
                  </Flex>
                </Box>
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}
